import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';

//create Subscription
export const createSubscription = createAsyncThunk(
  'subscription/createSubscription',
  async (subscriptionData, { dispatch }) => {
    try {
      const response = await apiClient.post(
        '/subscriptions',
        subscriptionData,
      );
      if (response.data.data.plan) {
        dispatch(setSelectedPlan(response.data.data.plan));
        dispatch(setBillingCycle(response.data.data.billingCycle));
      }
      return {
        status: response.status,
        data: {
          subscriptionId: response.data.data.subscriptionId,
          requiresPayment: response.data.data.requiresPayment,
          status: response.data.data.status,
          plan: response.data.data.plan,
          billingCycle: response.data.data.billingCycle,
          clientSecret: response.data.data.clientSecret
        }
      };
    } catch (error) {
      if (error.response?.data?.message) {
        throw error.response.data;
      }
      throw error;
    }
  }
);

// plan selection
export const selectPlanAsync = createAsyncThunk(
  'subscription/selectPlan',
  async (plan, { dispatch }) => {
    dispatch(setSelectedPlan(plan));
    return plan;
  }
);

//Create Payment Intent
export const createPaymentIntent = createAsyncThunk(
  'subscription/createPaymentIntent',
  async ({ subscriptionId, planKey, billingCycle }, { rejectWithValue }) => {
    try { 
      if (!planKey || !billingCycle) {
        throw new Error('Missing required plan details');
      }

      const response = await apiClient.post(
        '/subscriptions/create-payment-intent',
        {
          subscriptionId,
          planKey,
          billingCycle
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to create payment intent'
      );
    }
  }
);
//Confirm Payment Thunk
export const confirmPayment = createAsyncThunk(
  'subscription/confirmPayment',
  async ({ paymentIntentId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        '/subscriptions/confirm-payment',
        { paymentIntentId }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch subscription data
export const fetchSubscriptionData = createAsyncThunk(
  'subscription/fetchSubscriptionData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/subscriptions');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

//Start Trial 
export const startTrial = createAsyncThunk(
  'subscription/startTrial',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        '/subscriptions/start-trial',
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// cancel Subscription
export const cancelSubscription = createAsyncThunk(
  'subscription/cancelSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        '/subscriptions/cancel',
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
//Leave Subscription
export const leaveSubscription = createAsyncThunk(
  'subscription/leaveSubscription',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        '/subscriptions/leave',
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Redirect to Billing Portal Thunk
export const redirectToBillingPortal = createAsyncThunk(
  'subscription/redirectToBillingPortal',
  async (_, { rejectWithValue }) => {
    try {
       const response = await apiClient.get(
        '/subscriptions/billing-portal');
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to redirect to billing portal'
      );
    }
  }
);

// Get Billing Portal URL
export const getBillingPortalUrl = createAsyncThunk(
  'subscription/getBillingPortalUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        '/subscriptions/billing-portal-url',
        {}
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to get billing portal URL'
      );
    }
  }
);

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState: {
    selectedPlan: null,
    currentPlan: null,
    loading: false,
    error: null,
    clientSecret: null,
    paymentStatus: null,
    billingCycle:null
  },
  reducers: {
    setSelectedPlan: (state, action) => {
      state.selectedPlan = action.payload;
    },
    setBillingCycle: (state, action) => {
      state.billingCycle = action.payload;
    },
    resetPaymentState: (state) => {
      state.clientSecret = null;
      state.paymentStatus = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSubscription.fulfilled, (state, action) => {
          state.loading = false;
          state.selectedPlan = action.payload.data.plan;
          state.billingCycle = action.payload.data.billingCycle;
        })
      .addCase(createPaymentIntent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.loading = false;
        state.clientSecret = action.payload.clientSecret;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(confirmPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(confirmPayment.fulfilled, (state) => {
        state.loading = false;
        state.paymentStatus = 'succeeded';
      })
      .addCase(confirmPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.paymentStatus = 'failed';
      })
      .addCase(fetchSubscriptionData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPlan = action.payload.data;
      })
      .addCase(fetchSubscriptionData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(startTrial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startTrial.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPlan = {
          ...action.payload,
          isTrial: true
        };
      })
      .addCase(startTrial.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPlan = action.payload.data;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Redirect to billing portal
      .addCase(redirectToBillingPortal.pending, (state) => {
        state.billingPortalLoading = true;
        state.error = null;
      })
      .addCase(redirectToBillingPortal.fulfilled, (state) => {
        state.billingPortalLoading = false;
      })
      .addCase(redirectToBillingPortal.rejected, (state, action) => {
        state.billingPortalLoading = false;
        state.error = action.payload;
      })
      
      // Get billing portal URL
      .addCase(getBillingPortalUrl.pending, (state) => {
        state.billingPortalLoading = true;
        state.error = null;
      })
      .addCase(getBillingPortalUrl.fulfilled, (state, action) => {
        state.billingPortalLoading = false;
        state.billingPortalUrl = action.payload.url;
      })
      .addCase(getBillingPortalUrl.rejected, (state, action) => {
        state.billingPortalLoading = false;
        state.error = action.payload;
      });
  }
});

export const {
  resetError,
  setSelectedPlan,
  setBillingCycle,
  resetPaymentState,
  selectPlan 
} = subscriptionSlice.actions;

// Export selectors
export const selectSubscriptionState = (state) => state.subscription;
export const selectPaymentIntent = (state) => state.subscription.paymentIntent;
export const selectClientSecret = (state) => state.subscription.clientSecret;
export const selectPaymentStatus = (state) => state.subscription.paymentStatus;
export const selectSelectedPlan = (state) => state.subscription.selectedPlan;
export const selectBillingCycle = (state) => state.subscription.billingCycle;
export const selectSubcriptionLoading = (state) => state.subscription.loading;
export const selectError = (state) => state.subscription.error;
export const selectCurrentPlan = (state) => {
  const subscription = state.subscription.currentPlan;
  if (!subscription) return null;
  return subscription;
};

export default subscriptionSlice.reducer;